import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isEmpty, find } from 'lodash';
import getLocalized from 'utils/get-localized';
import scaleSize from 'utils/scale-size';
import useOnBackClickHandler from 'hooks/useOnBackClickHandler';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import TabPanel from 'components/TabPanel';
import SubTabPanel from 'components/SubTabPanel';
import CustomCollection from 'components/CustomCollection';
import { StyledSectionTitle } from './StyledComponents';

const TABS_SECTION_NAME = 'TABS_PANEL';
const TABS_ID = 'tabs_panel';

const SUB_TABS_SECTION_NAME = 'SUB_TABS_PANEL';
const SUB_TABS_ID = 'sub-tabs_panel';

const SUB_TABS_NAV_CONFIG = {
  enterTo: 'last-focused',
  leaveFor: {
    left: `@${TABS_SECTION_NAME}`,
    right: '',
  },
};
const TabContent = (props) => {
  const history = useHistory();
  const { title, tabs, detailsUrl, collections, isLoading, isFoldedByDefault } = props;
  const { onTabEnter, onSubTabEnter, onContentEnter } = props;
  const [isTabsFolded, setIsTabsFolded] = useState(isFoldedByDefault || false);

  const onContentFocus = useCallback(() => {
    setIsTabsFolded(true);
  }, []);

  const onTabPanelFocus = useCallback(() => {
    setIsTabsFolded(false);
  }, []);

  useOnBackClickHandler(() => {
    // If tabs folded - pressing back will unfold them
    // Otherwise - just a normal behavior
    if (isTabsFolded) {
      setIsTabsFolded(false);
    } else {
      history.goBack();
    }
  });

  // set focus to tab/sub-tab if there is no content
  useEffect(() => {
    const isCurrentTabHasSubTabs = !find(tabs, { detailsUrl });

    if (isLoading === false && isEmpty(collections)) {
      const sectionNameToFocus = isCurrentTabHasSubTabs ? SUB_TABS_SECTION_NAME : TABS_SECTION_NAME;
      const sectionIdToFocus = isCurrentTabHasSubTabs ? SUB_TABS_ID : TABS_ID;
      SpatialNavigationWrapper.focus(`@${sectionNameToFocus}`);
      SpatialNavigationWrapper.focus(`#${sectionIdToFocus} .active`);
    }
  }, [isLoading, collections, tabs, detailsUrl]);

  return (
    <TabPanel
      sectionName={TABS_SECTION_NAME}
      id={TABS_ID}
      title={<StyledSectionTitle>{title}</StyledSectionTitle>}
      onEnter={onTabEnter}
      onFocus={onTabPanelFocus}
      onUnfoldClick={onTabPanelFocus}
      defaultTabId={tabs[0].id}
      startScrollIndex={4}
      viewSize={6}
      isFolded={isTabsFolded}
      small
    >
      {tabs.map((subDashboard) => {
        const title = getLocalized(subDashboard.titles, subDashboard.title);
        const subTabs = subDashboard.subDashboards;

        return (
          <TabPanel.Tab
            key={subDashboard.id}
            id={subDashboard.id}
            title={title}
            path={`${subDashboard.id}`}
          >
            {isEmpty(subTabs) ? (
              <CustomCollection
                collections={collections}
                isLoading={isLoading}
                onContentEnter={onContentEnter}
                onContentFocus={onContentFocus}
                startListScrollIndex={1}
                top={scaleSize(170)}
                left={scaleSize(113)}
                // Collection MUST be default element if tab is folded, otherwise it causes bugs
                // https://hootko.atlassian.net/browse/STVWL-455
                disableDefaultFocus={!isTabsFolded}
              />
            ) : (
              <SubTabPanel
                sectionName={SUB_TABS_SECTION_NAME}
                id={SUB_TABS_ID}
                subTabs={subTabs}
                onEnter={onSubTabEnter}
                config={SUB_TABS_NAV_CONFIG}
              >
                {subTabs.map(({ id }) => {
                  return (
                    <SubTabPanel.Tab key={id} path={`${id}`}>
                      <CustomCollection
                        collections={collections}
                        isLoading={isLoading}
                        onContentEnter={onContentEnter}
                        onContentFocus={onContentFocus}
                        startListScrollIndex={1}
                        top={scaleSize(260)}
                        left={scaleSize(113)}
                        small
                      />
                    </SubTabPanel.Tab>
                  );
                })}
              </SubTabPanel>
            )}
          </TabPanel.Tab>
        );
      })}
    </TabPanel>
  );
};

TabContent.propTypes = {
  title: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  detailsUrl: PropTypes.string,
  collections: PropTypes.array,
  isLoading: PropTypes.bool,
  onTabEnter: PropTypes.func.isRequired,
  onSubTabEnter: PropTypes.func.isRequired,
  onContentEnter: PropTypes.func.isRequired,
};

export default TabContent;
